import { Trash } from "@phosphor-icons/react";
import { useState } from "react";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useAudienceMutations from "../../../../datahooks/useAudienceMutations";

interface RemoveTargetsProps {
  selectedProfiles: string[];
  onSuccess: () => void;
}

export default function RemoveTargets({
  selectedProfiles,
  onSuccess,
}: RemoveTargetsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const isDesktop = useTwBreakpoint("lg");

  const { removeProfiles, isRemovingProfiles } = useAudienceMutations();

  function onRemove() {
    removeProfiles({ targetUrns: selectedProfiles }).then(() => {
      onSuccess();
      setIsOpen(false);
    });
  }

  const selectedProfilesCount = selectedProfiles.length;
  const suffix = selectedProfilesCount > 1 ? "s" : "";

  const Component = isDesktop ? Dialog : Drawer;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Close = isDesktop ? DialogClose : DrawerClose;
  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  return (
    <Component open={isOpen} onOpenChange={setIsOpen}>
      <Trigger asChild>
        <Button
          leftIcon={<Trash />}
          variant="outline-danger"
          className="max-lg:flex-1"
        >
          <span className="truncate">
            Remove {selectedProfilesCount} Target{suffix}
          </span>
        </Button>
      </Trigger>

      <Content className="lg:w-[448px]">
        <Title>Remove targets?</Title>
        <Description className="mb-12">
          Are you sure you want to remove {selectedProfilesCount} target
          {suffix} from this campaign
        </Description>

        <div className="flex flex-col gap-2 lg:flex-row-reverse lg:gap-4">
          <Button
            className="flex-1"
            size={isDesktop ? "lg" : "lg"}
            onClick={onRemove}
            isLoading={isRemovingProfiles}
            variant="outline-danger"
          >
            Remove
          </Button>
          <Close asChild>
            <Button
              variant="secondary-black"
              className="flex-1"
              size={isDesktop ? "lg" : "lg"}
              onClick={() => setIsOpen(false)}
              disabled={isRemovingProfiles}
            >
              Cancel
            </Button>
          </Close>
        </div>
      </Content>
    </Component>
  );
}
